import { JSX, useContext, useState, useEffect, useRef } from "react";
import { BulkTargetingWizardContext } from "../TargetingWizardContext";
import { ColumnConfigurationReport } from "../interfaces";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ExportColumn } from "@app/report-customization/build-reports/interfaces/exportColumns";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";
import { useBulkTargetingModel } from "@app/targeting/import/useBulkTargetingModel";
import ExportService from "@app/exports/exportService";
import { ColumnConfigurationSection } from "@app/report-customization/build-reports/components/ColumnConfigurationSection";
import { toast } from "react-toastify";

interface ReportColumnConfigurationProps {
  columnsEndpoint: () => Promise<ExportColumn[]>;
  reportType: ReportType;
  sectionKey: string;
  title: string;
}

const defaultPreset: ColumnConfigurationPresetOption = { id: -1, isPrivate: false, name: "Default - All Columns" };

const ReportColumnConfiguration = ({ reportType, sectionKey, title, columnsEndpoint }: ReportColumnConfigurationProps): JSX.Element => {
  const wizardContext = useContext(BulkTargetingWizardContext);
  const [selectedPreset, setSelectedPreset] = useState<ColumnConfigurationPresetOption>(defaultPreset);
  const exportService = useRef(new ExportService()).current;
  const model = useBulkTargetingModel();
  const presets = wizardContext.reportPresets[reportType] ?? [defaultPreset];

  useEffect(() => {
    // Paired Service Area/Opportunity sections share this reportType's preset pool; skip the fetch if a
    // sibling section already loaded it (or a previous mount/toggle did), instead of calling the API twice.
    if (wizardContext.reportPresets[reportType]) {
      return;
    }

    exportService.getColumnCustomizationPresets(reportType)
      .then(presets => wizardContext.setReportPresets(reportType, [defaultPreset, ...presets]))
      .catch(() => {
        toast.error(`Failed to load column configuration presets for ${title} report. Please try again later.`, {
          containerId: "z-index-9999",
        });
      });
  }, [reportType, exportService]);

  const handlePresetsRefresh = async () => {
    const presets = await exportService.getColumnCustomizationPresets(reportType);
    wizardContext.setReportPresets(reportType, [defaultPreset, ...presets]);
  };

  const handlePresetChange = (preset: ColumnConfigurationPresetOption) => {
    setSelectedPreset(preset);

    if (preset.id === -1) {
      const updatedConfigurations = { ...wizardContext.columnConfigurations };
      delete updatedConfigurations[sectionKey];
      wizardContext.setColumnConfigurations(updatedConfigurations);
    } else {
      const newConfig: ColumnConfigurationReport = {
        columnsEndpoint: columnsEndpoint,
        key: sectionKey,
        presetId: preset.id.toString(),
        reportType: reportType,
        title: preset.name,
      };
      wizardContext.setColumnConfigurations({
        ...wizardContext.columnConfigurations,
        [sectionKey]: newConfig,
      });
    }
  };

  return (
    <>
      <div className="terminations-wizard__card-title">{title}</div>
      <ColumnConfigurationSection
        hideHeading={true}
        columnsEndpoint={columnsEndpoint}
        exportModel={model}
        presets={presets}
        selectedPreset={selectedPreset}
        reportType={reportType}
        onPresetChange={handlePresetChange}
        onPresetsRefresh={handlePresetsRefresh}
      />
    </>
  );
};

export default ReportColumnConfiguration;
